import { useEffect, useRef, useState, type MouseEvent } from 'react'
import type { CashPolicyResult } from '../engine/types'
import { formatCurrency } from '../projectionsFormat'

type PolicyChartProps = {
  months: CashPolicyResult['months']
  effectiveFloor: number
  height?: number
  showDistributions?: boolean
}

const X_TICK_TS = [1, 6, 12, 18, 24, 30, 36]

const CASH_COLOR = 'text-blue-700'
const MM_COLOR = 'text-emerald-600'

type TooltipSeriesKey = 'cash' | 'floor' | 'mm' | 'distributions'

/** Short axis label: $1.2M / $412k / $0. Full precision lives in the month table. */
function axisLabel(value: number): string {
  const abs = Math.abs(value)
  const sign = value < 0 ? '-' : ''
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`
  if (abs >= 1_000) return `${sign}$${Math.round(abs / 1_000)}k`
  return `${sign}$${Math.round(abs)}`
}

function niceStep(rawStep: number): number {
  if (!Number.isFinite(rawStep) || rawStep <= 0) return 1
  const magnitude = 10 ** Math.floor(Math.log10(rawStep))
  const normalized = rawStep / magnitude
  if (normalized <= 1) return magnitude
  if (normalized <= 2) return 2 * magnitude
  if (normalized <= 5) return 5 * magnitude
  return 10 * magnitude
}

function niceTicks(min: number, max: number, targetCount = 5): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [0]
  if (min === max) return [min]
  const step = niceStep((max - min) / Math.max(targetCount - 1, 1))
  const start = Math.floor(min / step) * step
  const end = Math.ceil(max / step) * step
  const ticks: number[] = []
  for (let value = start; value <= end + step * 0.001; value += step) {
    ticks.push(Math.round(value))
  }
  return ticks.length > 0 ? ticks : [min, max]
}

/** Never return a zero-width domain: a flat series must still land mid-plot. */
function padDomain(lo: number, hi: number): [number, number] {
  const span = hi - lo
  if (span > 0) {
    const pad = span * 0.15
    return [lo - pad, hi + pad]
  }
  const pad = Math.max(Math.abs(hi) * 0.15, 1000)
  return [lo - pad, hi + pad]
}

function nearestIndex(mouseX: number, plotLeft: number, plotWidth: number, count: number): number {
  if (count <= 1) return 0
  const ratio = (mouseX - plotLeft) / plotWidth
  const index = Math.round(ratio * (count - 1))
  return Math.max(0, Math.min(count - 1, index))
}

/** Projection month keys are YYYY-MM; label tooltips with calendar month-end. */
function monthEndDateLabel(monthKey: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(monthKey.trim())
  if (!match) {
    return monthKey
  }
  const year = Number(match[1])
  const month = Number(match[2])
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
    return monthKey
  }
  const lastDay = new Date(year, month, 0).getDate()
  return `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`
}

export default function PolicyChart({
  months,
  effectiveFloor,
  height = 440,
  showDistributions = false,
}: PolicyChartProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(640)
  const [hoverIndex, setHoverIndex] = useState<number | null>(null)
  const [hoverSeries, setHoverSeries] = useState<TooltipSeriesKey>('cash')
  const [tooltipPos, setTooltipPos] = useState<{ left: number; top: number } | null>(null)

  useEffect(() => {
    const element = containerRef.current
    if (!element || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver((entries) => {
      const nextWidth = entries[0]?.contentRect.width
      if (nextWidth != null && nextWidth > 0) {
        setWidth(Math.round(nextWidth))
      }
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (hoverIndex == null) {
      setTooltipPos(null)
      return
    }
    const container = containerRef.current
    const tooltip = tooltipRef.current
    if (!container || !tooltip) return

    const containerRect = container.getBoundingClientRect()
    const tooltipRect = tooltip.getBoundingClientRect()
    const padding = 8
    const preferredLeft = containerRect.width / 2 - tooltipRect.width / 2
    const preferredTop = padding
    const maxLeft = containerRect.width - tooltipRect.width - padding
    const left = Math.max(padding, Math.min(preferredLeft, maxLeft))
    const top = Math.max(padding, preferredTop)
    setTooltipPos({ left, top })
  }, [hoverIndex, width])

  if (months.length === 0) {
    return null
  }

  const operating = months.map((m) => m.operating_cash)
  const mm = months.map((m) => m.mm_balance)
  const distributions = months.map((m) => Math.abs(m.distributions))
  const cashLo = Math.max(
    0,
    Math.min(effectiveFloor, ...operating, ...(showDistributions ? distributions : [])),
  )
  const cashHi = Math.max(
    effectiveFloor,
    ...operating,
    ...(showDistributions ? distributions : []),
  )
  const mmHi = Math.max(0, ...mm)

  const cashDomain = padDomain(cashLo, cashHi)
  const mmTop = mmHi > 0 ? mmHi * 1.15 : 1000

  const yTicks = niceTicks(Math.max(0, cashDomain[0]), cashDomain[1], 5)
  const yDomain: [number, number] = [
    Math.max(0, yTicks[0] ?? cashDomain[0]),
    yTicks[yTicks.length - 1] ?? cashDomain[1],
  ]
  const mmTicks = niceTicks(0, mmTop, 5)
  const mmPlotDomain: [number, number] = [0, mmTicks[mmTicks.length - 1] ?? mmTop]

  const padTop = 24
  const padBottom = 26
  const padLeft = 56
  const padRight = 52
  const plotLeft = padLeft
  const plotRight = width - padRight
  const plotTop = padTop
  const plotBottom = height - padBottom
  const plotWidth = plotRight - plotLeft
  const plotHeight = plotBottom - plotTop

  const x = (index: number) =>
    months.length === 1 ? plotLeft : plotLeft + (index / (months.length - 1)) * plotWidth
  const scale = (value: number, domain: [number, number]) =>
    plotBottom - ((value - domain[0]) / (domain[1] - domain[0])) * plotHeight
  const yCash = (value: number) => scale(value, yDomain)
  const yMm = (value: number) => scale(value, mmPlotDomain)

  const path = (values: number[], y: (value: number) => number) =>
    values
      .map((value, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(value).toFixed(1)}`)
      .join(' ')

  const operatingPath = path(operating, yCash)
  const mmPath = path(mm, yMm)
  const floorY = yCash(effectiveFloor)
  const slotWidth = months.length === 1 ? plotWidth : plotWidth / (months.length - 1)
  const distributionBarWidth = Math.min(24, slotWidth * 0.55)

  const shortfallPath =
    `${path(operating.map((value) => Math.min(value, effectiveFloor)), yCash)} ` +
    `L${x(months.length - 1).toFixed(1)},${floorY.toFixed(1)} ` +
    `L${plotLeft.toFixed(1)},${floorY.toFixed(1)} Z`

  const xTickIndices = X_TICK_TS.filter((t) => t <= months.length).map((t) => t - 1)

  const handleMouseMove = (event: MouseEvent<SVGSVGElement>) => {
    const svg = event.currentTarget
    const rect = svg.getBoundingClientRect()
    const scaleX = width / rect.width
    const scaleY = height / rect.height
    const mouseX = (event.clientX - rect.left) * scaleX
    const mouseY = (event.clientY - rect.top) * scaleY
    if (mouseX < plotLeft || mouseX > plotRight) {
      setHoverIndex(null)
      return
    }
    const index = nearestIndex(mouseX, plotLeft, plotWidth, months.length)
    const cashY = yCash(operating[index] ?? 0)
    const mmY = yMm(mm[index] ?? 0)
    const distY = showDistributions ? yCash(distributions[index] ?? 0) : Infinity
    setHoverIndex(index)
    const nearest = [
      { key: 'cash' as const, distance: Math.abs(mouseY - cashY) },
      { key: 'mm' as const, distance: Math.abs(mouseY - mmY) },
      ...(showDistributions
        ? [{ key: 'distributions' as const, distance: Math.abs(mouseY - distY) }]
        : []),
    ].sort((a, b) => a.distance - b.distance)
    setHoverSeries(nearest[0]?.key ?? 'cash')
  }

  const hoverTracerY =
    hoverIndex == null
      ? null
      : hoverSeries === 'cash'
        ? yCash(operating[hoverIndex] ?? 0)
        : hoverSeries === 'distributions'
          ? yCash(distributions[hoverIndex] ?? 0)
          : yMm(mm[hoverIndex] ?? 0)

  const hoveredMonth = hoverIndex == null ? null : months[hoverIndex]
  const tooltipValues =
    hoveredMonth == null
      ? null
      : {
          cash: hoveredMonth.operating_cash,
          floor: effectiveFloor,
          mm: hoveredMonth.mm_balance,
          distributions: Math.abs(hoveredMonth.distributions),
        }

  const tooltipSeries: Array<{
    key: TooltipSeriesKey
    label: string
    colorClass: string
  }> = [
    { key: 'cash', label: 'Operating cash', colorClass: CASH_COLOR },
    { key: 'floor', label: 'Cash Floor', colorClass: CASH_COLOR },
    ...(showDistributions
      ? [{ key: 'distributions' as const, label: 'Distributions', colorClass: CASH_COLOR }]
      : []),
    { key: 'mm', label: 'Money market', colorClass: MM_COLOR },
  ]

  return (
    <figure className="relative m-0 w-full" ref={containerRef}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
        className="block w-full rounded border border-gray-200 bg-white"
        role="img"
        aria-label="Operating cash, money market, and floor over 36 months"
        preserveAspectRatio="none"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => {
          setHoverIndex(null)
          setHoverSeries('cash')
        }}
      >
        <text
          x={plotLeft}
          y={12}
          textAnchor="start"
          className={`fill-current text-[10px] font-medium ${CASH_COLOR}`}
        >
          Operating cash
        </text>
        <text
          x={plotRight}
          y={12}
          textAnchor="end"
          className={`fill-current text-[10px] font-medium ${MM_COLOR}`}
        >
          Money market
        </text>

        {yTicks.map((value) => (
          <g key={`y-grid-${value}`}>
            <line
              x1={plotLeft}
              x2={plotRight}
              y1={yCash(value)}
              y2={yCash(value)}
              stroke="currentColor"
              strokeWidth="1"
              className="text-gray-200"
            />
            <text
              x={plotLeft - 6}
              y={yCash(value) + 3}
              textAnchor="end"
              className={`fill-current text-[10px] tabular-nums ${CASH_COLOR}`}
            >
              {axisLabel(value)}
            </text>
            <text
              x={plotRight + 6}
              y={yCash(value) + 3}
              textAnchor="start"
              className={`fill-current text-[10px] tabular-nums ${MM_COLOR}`}
            >
              {axisLabel(
                mmPlotDomain[0] +
                  ((value - yDomain[0]) / (yDomain[1] - yDomain[0])) *
                    (mmPlotDomain[1] - mmPlotDomain[0]),
              )}
            </text>
          </g>
        ))}

        {xTickIndices.map((i) => (
          <line
            key={`x-grid-${i}`}
            x1={x(i)}
            x2={x(i)}
            y1={plotTop}
            y2={plotBottom}
            stroke="currentColor"
            strokeWidth="1"
            className="text-gray-200"
          />
        ))}

        <path
          d={shortfallPath}
          fill="currentColor"
          stroke="currentColor"
          strokeWidth="1"
          className="fill-amber-100 stroke-amber-600"
        />

        {showDistributions
          ? distributions.map((value, index) => {
              if (value <= 0) {
                return null
              }
              const barTop = yCash(value)
              const barHeight = plotBottom - barTop
              if (barHeight <= 0) {
                return null
              }
              return (
                <rect
                  key={`distribution-bar-${index}`}
                  x={x(index) - distributionBarWidth / 2}
                  y={barTop}
                  width={distributionBarWidth}
                  height={barHeight}
                  fill="currentColor"
                  className={`${CASH_COLOR} opacity-80`}
                />
              )
            })
          : null}
        <path
          d={mmPath}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className={MM_COLOR}
        />
        <path
          d={operatingPath}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          className={CASH_COLOR}
        />
        <line
          x1={plotLeft}
          x2={plotRight}
          y1={floorY}
          y2={floorY}
          stroke="currentColor"
          strokeWidth="1.25"
          strokeDasharray="5 4"
          className={CASH_COLOR}
        />
        <text
          x={plotRight}
          y={floorY - 5}
          textAnchor="end"
          className={`fill-current text-[10px] font-medium ${CASH_COLOR}`}
        >
          Cash floor
        </text>

        {hoverIndex != null && hoverTracerY != null ? (
          <>
            <line
              x1={x(hoverIndex)}
              x2={x(hoverIndex)}
              y1={plotTop}
              y2={plotBottom}
              stroke="currentColor"
              strokeWidth="1"
              strokeDasharray="3 3"
              className="text-gray-400"
            />
            <line
              x1={plotLeft}
              x2={plotRight}
              y1={hoverTracerY}
              y2={hoverTracerY}
              stroke="currentColor"
              strokeWidth="1"
              strokeDasharray="3 3"
              className="text-gray-400"
            />
          </>
        ) : null}

        <line
          x1={plotLeft}
          x2={plotRight}
          y1={plotBottom}
          y2={plotBottom}
          stroke="currentColor"
          strokeWidth="1"
          className="text-gray-300"
        />

        {xTickIndices.map((i) => {
          const t = i + 1
          const anchor = t === 1 ? 'start' : t === months.length ? 'end' : 'middle'
          return (
            <text
              key={`x-${t}`}
              x={x(i)}
              y={plotBottom + 16}
              textAnchor={anchor}
              className="fill-gray-500 text-[10px] tabular-nums"
            >
              {months[i]?.month ?? ''}
            </text>
          )
        })}
      </svg>

      {hoveredMonth && tooltipValues ? (
        <div
          ref={tooltipRef}
          className="pointer-events-none absolute z-10 min-w-[12rem] rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow-lg"
          style={{
            left: tooltipPos?.left ?? 8,
            top: tooltipPos?.top ?? 8,
            visibility: tooltipPos ? 'visible' : 'hidden',
          }}
        >
          <p className="mb-2 font-medium text-gray-900">{monthEndDateLabel(hoveredMonth.month)}</p>
          <dl className="space-y-1">
            {tooltipSeries.map((series) => (
              <div key={series.key} className="flex items-baseline justify-between gap-4">
                <dt className={series.colorClass}>{series.label}</dt>
                <dd className="tabular-nums text-gray-900">
                  {formatCurrency(tooltipValues[series.key])}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      ) : null}

      <figcaption className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <span className={`flex items-center gap-1.5 ${CASH_COLOR}`}>
          <span className="inline-block h-0 w-4 border-t-2 border-current" />
          Operating cash
        </span>
        {showDistributions ? (
          <span className={`flex items-center gap-1.5 ${CASH_COLOR}`}>
            <span className="inline-block h-3 w-3 rounded-sm bg-current opacity-80" />
            Distributions
          </span>
        ) : null}
        <span className={`flex items-center gap-1.5 ${MM_COLOR}`}>
          <span className="inline-block h-0 w-4 border-t-2 border-current" />
          Money market
        </span>
      </figcaption>
    </figure>
  )
}
